/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef, type ReactNode, type RefObject } from 'react';

import { cn } from '@/lib/cn';

/** Controlled dropdown; parent owns open state for mutual exclusion. */
export function Menu({
  open,
  onOpenChange,
  trigger,
  children,
  className,
  handoffFrom,
}: {
  /** Extra class on the wrapper, e.g. to show or hide the whole menu responsively. */
  className?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: (state: { open: boolean; toggle: () => void }) => ReactNode;
  children: ReactNode;
  /**
   * Set when the menu was opened from a stand-in rather than its own trigger: a folded menu opened
   * from "More" while its own trigger is hidden. Focus then moves into the menu, to its current
   * option or else its first, and goes back to the stand-in when the menu closes.
   */
  handoffFrom?: RefObject<HTMLElement | null>;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onOpenChange]);

  useEffect(() => {
    const pop = popRef.current;
    if (!open || !handoffFrom || !pop) return;
    const standIn = handoffFrom.current;
    const option =
      pop.querySelector<HTMLElement>('[aria-current="true"]') ??
      pop.querySelector<HTMLElement>('button:not(:disabled), a[href]');
    option?.focus();
    return () => {
      // The option that held focus is gone with the menu, which would leave focus on the page -
      // unless the user has already put it somewhere else, such as a field they clicked.
      const active = document.activeElement;
      if (!active || active === document.body || pop.contains(active)) {
        standIn?.focus();
      }
    };
  }, [open, handoffFrom]);

  return (
    <div className={cn('menu', className)} ref={ref}>
      {trigger({ open, toggle: () => onOpenChange(!open) })}
      {open && (
        <div className="menu-pop" ref={popRef}>
          {children}
        </div>
      )}
    </div>
  );
}
